import type { SpecialServiceResponseMapped } from '@models/landing/api/special-service.model';
import type { CodeTypeList } from '@models/master-data/master-data.model';
import { PassengerTypeCode, SsrType } from '@models/common/shared-enums.model';
import type { SSRModalCardSelectionProps } from '@fz/ui-components-react';
import { getResource } from '@utils/resource-translator.util';
import {
  isInfantPassengerTypeLabel,
  resolvePassengerTypeLabel,
  sortPassengersWithAssignedInfants,
} from '@shared/components/ssr-passenger.util';
import {
  isMatchingLoungeTab,
  parseLoungeAmount,
  resolveCurrencyDecimalPlaces,
  resolveLoungeTabValue,
  resolveLoungeCodeItem,
  resolveLoungeInfoPurchased,
  resolveUniqueLoungeQuotes,
} from '@shared/components/LoungeSsrModalComponent/lounge-options.util';
import { IconLoungeModal } from '@shared/icons';

const DEFAULT_CONFIRM_LABEL = 'Confirm.Text';
const MOST_POPULAR_BADGE_TEXT_KEY = 'ContactDetails.Popular.Text';
const DEFAULT_TITLE = 'SpecialService.Text';
const DEFAULT_CONTENT_TITLE = 'Extras.SpecialService.Header.Text';
const DEFAULT_CONTENT_DESCRIPTION = 'Extras.SpecialService.lounge.SubTitle.Text';
const DEFAULT_INFO_BANNER_TEXT = 'Extras.SpecialService.Lounge.VAT.Text';
const DEFAULT_LOUNGE_INCLUDED_TITLE = 'Extras.Lounge.Included.Title.Text';
const DEFAULT_LOUNGE_INCLUDED_DESCRIPTION = 'Extras.Lounge.Included.Description.Text';
const DEFAULT_CLOSE_LABEL = 'Close.Text';
const LOUNGE_TITLE_RESOURCE_KEY = 'SelectPassenger.Lounge.Text';

export interface LoungeAddApiPayload {
  [key: string]: unknown;
  ssrType: string;
  logicalFlightId: string;
  physicalFlightId: string;
  amount: string;
  paxJourneyId: string;
  resPaxId: number;
  recordNumber: number;
  quantityAvailable: number;
  chargeComment: string;
  codeType: string;
  isBusiness: boolean;
  secureHash: string;
  taxInfo: {
    amount: string;
    rateDef: string;
    code: string;
  };
  totalAmount: string;
}

type LoungeModalPassenger = SSRModalCardSelectionProps['passengers'][number];
type LoungeModalTabItem = NonNullable<SSRModalCardSelectionProps['flightTabItems']>[number];
interface LoungeModalProps {
  title: SSRModalCardSelectionProps['title'];
  confirmButtonLabel: SSRModalCardSelectionProps['confirmButtonLabel'];
  currencyCode: SSRModalCardSelectionProps['currencyCode'];
  contentTitle: SSRModalCardSelectionProps['contentTitle'];
  contentDescription: SSRModalCardSelectionProps['contentDescription'];
  infoBannerText: SSRModalCardSelectionProps['infoBannerText'];
  passengers: SSRModalCardSelectionProps['passengers'];
  passengerSSRDetails: NonNullable<SSRModalCardSelectionProps['passengerSSRDetails']>;
  flightTabItems: NonNullable<SSRModalCardSelectionProps['flightTabItems']>;
  options: SSRModalCardSelectionProps['options'];
  isUnavailable: SSRModalCardSelectionProps['isUnavailable'];
  unavailableTitle: SSRModalCardSelectionProps['unavailableTitle'];
  unavailableDescription: SSRModalCardSelectionProps['unavailableDescription'];
  unavailableVariant: SSRModalCardSelectionProps['unavailableVariant'];
  unavailableIcon: SSRModalCardSelectionProps['unavailableIcon'];
}

const LOUNGE_SSR_TYPE = String(SsrType.LOUNGE);
const INFANT_PASSENGER_TYPE = Number(PassengerTypeCode.Infant);

const resolveDisplayPassengerMap = (
  specialService?: SpecialServiceResponseMapped,
): Record<number, { seatNumber: string; pnrNumber: string }> => {
  if (!specialService?.LoungeQuotes || specialService.LoungeQuotes.length === 0) {
    return {};
  }

  return specialService.LoungeQuotes.reduce<
    Record<number, { seatNumber: string; pnrNumber: string }>
  >((acc, quote) => {
    if (!quote.LoungeInfo) return acc;

    quote.LoungeInfo.forEach((info) => {
      if (!info.passengerDetails) return;

      info.passengerDetails.forEach((detail) => {
        if (!acc[detail.resPaxId]) {
          acc[detail.resPaxId] = {
            seatNumber: detail.seatNumber ?? '',
            pnrNumber: detail.pnrNumber ?? '',
          };
        }
      });
    });

    return acc;
  }, {});
};

const mapPassengers = (specialService?: SpecialServiceResponseMapped): LoungeModalPassenger[] => {
  const displayPassengerMap = resolveDisplayPassengerMap(specialService);

  // If passengerList exists and has data, use it
  if (specialService?.passengerList && specialService.passengerList.length > 0) {
    return sortPassengersWithAssignedInfants(specialService.passengerList).map((passenger) => ({
      id: passenger.resPaxId.toString(),
      name: `${passenger.title} ${passenger.firstName} ${passenger.lastName}`
        .replace(/\s+/g, ' ')
        .trim(),
      type: resolvePassengerTypeLabel(passenger.passengerType),
      isInfant: Number(passenger.passengerType) === INFANT_PASSENGER_TYPE,
      flights: [],
      seatNumber: displayPassengerMap[passenger.resPaxId]?.seatNumber ?? '',
      pnrNumber: displayPassengerMap[passenger.resPaxId]?.pnrNumber,
    }));
  }

  // Fallback: derive passengers from LoungeInfo.passengerDetails if passengerList is empty/missing
  const passengersFromLounge = new Map<
    number,
    { name: string; seatNumber: string; pnrNumber?: string }
  >();

  specialService?.LoungeQuotes.forEach((quote) => {
    quote.LoungeInfo.forEach((loungeInfo) => {
      loungeInfo.passengerDetails.forEach((detail) => {
        if (!passengersFromLounge.has(detail.resPaxId)) {
          passengersFromLounge.set(detail.resPaxId, {
            name: `${detail.title} ${detail.firstName} ${detail.lastName}`
              .replace(/\s+/g, ' ')
              .trim(),
            seatNumber: detail.seatNumber ?? '',
            pnrNumber: detail.pnrNumber,
          });
        }
      });
    });
  });

  return Array.from(passengersFromLounge.entries()).map(([resPaxId, data]) => ({
    id: resPaxId.toString(),
    name: data.name,
    type: '',
    isInfant: false,
    flights: [],
    seatNumber: data.seatNumber,
    pnrNumber: data.pnrNumber,
  }));
};

const mapFlightTabItems = (specialService?: SpecialServiceResponseMapped): LoungeModalTabItem[] => {
  const loungeFlightTabItems = specialService?.loungeFlightTabItems ?? [];
  const uniqueQuotes = resolveUniqueLoungeQuotes(specialService);

  if (loungeFlightTabItems.length > 0) {
    return loungeFlightTabItems.map((flightTabItem, index) => ({
      value:
        flightTabItem.physicalFlightID ??
        uniqueQuotes[index]?.physicalFlightId ??
        flightTabItem.logicalFlightID ??
        uniqueQuotes[index]?.logicalFlightId ??
        flightTabItem.value,
      label: flightTabItem.label,
      content: flightTabItem.content,
    }));
  }

  return uniqueQuotes.map((quote, index) => ({
    value: resolveLoungeTabValue(quote.logicalFlightId, quote.physicalFlightId),
    label: index === 0 ? 'Departure' : 'Return',
    content: quote.flightNumber
      ? `t('Extras.Flight.Text') ${quote.flightNumber}`
      : quote.logicalFlightId,
  }));
};

const resolveOptionsForFlightTab = (
  specialService: SpecialServiceResponseMapped | undefined,
  codeTypeList: CodeTypeList | undefined,
  activeFlightTab: string,
  currencyDecimalPlaces: number,
  preferredServices?: readonly string[],
): SSRModalCardSelectionProps['options'] => {
  const quotes = resolveUniqueLoungeQuotes(specialService);
  const quotesForTab = activeFlightTab
    ? quotes.filter((quote) =>
        isMatchingLoungeTab(activeFlightTab, quote.logicalFlightId, quote.physicalFlightId),
      )
    : quotes;

  return quotesForTab.flatMap((quote) =>
    quote.LoungeInfo.map((loungeInfo) => {
      const codeItem = resolveLoungeCodeItem(loungeInfo.codeType, codeTypeList);
      const codeName = codeItem?.shortCodeName ?? '';
      const totalAmount = parseLoungeAmount(loungeInfo.totalAmount);

      // Check if this lounge codeType is in the preferredServices list
      const isPreferredService = preferredServices?.some(
        (serviceCode) => serviceCode === loungeInfo.codeType,
      );

      const mostPopularBadgeText = isPreferredService
        ? getResource(MOST_POPULAR_BADGE_TEXT_KEY)
        : undefined;

      return {
        id: `lounge-${loungeInfo.codeType}`,
        label: codeName,
        description: `${quote.currency} ${totalAmount.toFixed(currencyDecimalPlaces)}`,
        price: totalAmount,
        currency: quote.currency,
        col: 'col-6',
        isPurchased: resolveLoungeInfoPurchased(
          specialService,
          activeFlightTab,
          loungeInfo.codeType,
        ),
        icon: undefined,
        mostPopularBadgeText,
      };
    }),
  );
};

const mapOptions = (
  specialService?: SpecialServiceResponseMapped,
  codeTypeList?: CodeTypeList,
  preferredServices?: readonly string[],
  currencyDecimalPlaces?: number,
): SSRModalCardSelectionProps['options'] => {
  const initialFlightTabValue = mapFlightTabItems(specialService)[0]?.value ?? '';
  return resolveOptionsForFlightTab(
    specialService,
    codeTypeList,
    initialFlightTabValue,
    resolveCurrencyDecimalPlaces(currencyDecimalPlaces),
    preferredServices,
  );
};

const resolvePurchasedLoungeInfo = (
  pax: SpecialServiceResponseMapped['passengerList'][number],
  passengerResPaxId: number,
  codeTypeList?: CodeTypeList,
): string => {
  if (pax.resPaxId !== passengerResPaxId) {
    return '';
  }

  for (const flightDetail of pax.flightsDetail) {
    for (const extra of flightDetail.includedExtrasList) {
      if (
        extra.ssrType.toUpperCase() === LOUNGE_SSR_TYPE &&
        (extra.amount > 0 || extra.isPurchased)
      ) {
        const codeItem = codeTypeList?.find((item) => item.codeID === extra.codeType);
        if (codeItem?.shortCodeName) {
          return codeItem.shortCodeName;
        }
      }
    }
  }

  return '';
};

const mapPassengerSSRDetails = (
  passengers: LoungeModalPassenger[],
  specialService?: SpecialServiceResponseMapped,
  codeTypeList?: CodeTypeList,
): NonNullable<SSRModalCardSelectionProps['passengerSSRDetails']> => {
  return passengers.reduce<NonNullable<SSRModalCardSelectionProps['passengerSSRDetails']>>(
    (acc, passenger) => {
      const passengerResPaxId = Number(passenger.id);
      let purchasedLoungeInfo = '';

      specialService?.passengerList.forEach((pax) => {
        if (!purchasedLoungeInfo) {
          purchasedLoungeInfo = resolvePurchasedLoungeInfo(pax, passengerResPaxId, codeTypeList);
        }
      });

      acc[passenger.id] = isInfantPassengerTypeLabel(passenger.type)
        ? {
            subText1: '',
            subText2: '',
            infantSubText: getResource('Extras.NotApplSpecialService.Tooltip.Text'),
          }
        : { subText1: purchasedLoungeInfo, subText2: '' };
      return acc;
    },
    {},
  );
};

// eslint-disable-next-line complexity, max-lines-per-function
export const mapLoungeToModalView = (
  specialService?: SpecialServiceResponseMapped,
  codeTypeList?: CodeTypeList,
  preferredServices?: readonly string[],
  currencyDecimalPlaces?: number,
): LoungeModalProps => {
  const passengers = mapPassengers(specialService);
  const flightTabItems = mapFlightTabItems(specialService);
  const options = mapOptions(
    specialService,
    codeTypeList,
    preferredServices,
    currencyDecimalPlaces,
  );

  // Translate all resource keys
  const localizedTitle = getResource(LOUNGE_TITLE_RESOURCE_KEY);
  const defaultTitle = getResource(DEFAULT_TITLE);

  let resolvedTitle = DEFAULT_TITLE;
  if (localizedTitle && localizedTitle !== LOUNGE_TITLE_RESOURCE_KEY) {
    resolvedTitle = localizedTitle;
  } else if (defaultTitle && defaultTitle !== DEFAULT_TITLE) {
    resolvedTitle = defaultTitle;
  }

  const localizedConfirmLabel = getResource(DEFAULT_CONFIRM_LABEL);
  const resolvedConfirmLabel =
    localizedConfirmLabel && localizedConfirmLabel !== DEFAULT_CONFIRM_LABEL
      ? localizedConfirmLabel
      : DEFAULT_CONFIRM_LABEL;

  const localizedCloseLabel = getResource(DEFAULT_CLOSE_LABEL);
  const resolvedCloseLabel =
    localizedCloseLabel && localizedCloseLabel !== DEFAULT_CLOSE_LABEL
      ? localizedCloseLabel
      : DEFAULT_CLOSE_LABEL;

  const localizedContentTitle = getResource(DEFAULT_CONTENT_TITLE);
  const resolvedContentTitle =
    localizedContentTitle && localizedContentTitle !== DEFAULT_CONTENT_TITLE
      ? localizedContentTitle
      : DEFAULT_CONTENT_TITLE;

  const localizedContentDescription = getResource(DEFAULT_CONTENT_DESCRIPTION);
  const resolvedContentDescription =
    localizedContentDescription && localizedContentDescription !== DEFAULT_CONTENT_DESCRIPTION
      ? localizedContentDescription
      : DEFAULT_CONTENT_DESCRIPTION;

  const localizedInfoBannerText = getResource(DEFAULT_INFO_BANNER_TEXT);
  const resolvedInfoBannerText =
    localizedInfoBannerText && localizedInfoBannerText !== DEFAULT_INFO_BANNER_TEXT
      ? localizedInfoBannerText
      : DEFAULT_INFO_BANNER_TEXT;

  const localizedInCludedTitle = getResource(DEFAULT_LOUNGE_INCLUDED_TITLE);
  const resolvedIncludedTitle =
    localizedInCludedTitle && localizedInCludedTitle !== DEFAULT_LOUNGE_INCLUDED_TITLE
      ? localizedInCludedTitle
      : DEFAULT_LOUNGE_INCLUDED_TITLE;

  const localizedIncludedDescription = getResource(DEFAULT_LOUNGE_INCLUDED_DESCRIPTION);
  const resolvedIncludedDescription =
    localizedIncludedDescription &&
    localizedIncludedDescription !== DEFAULT_LOUNGE_INCLUDED_DESCRIPTION
      ? localizedIncludedDescription
      : DEFAULT_LOUNGE_INCLUDED_DESCRIPTION;

  const isUnavailable = options.length === 0;

  return {
    title: resolvedTitle,
    confirmButtonLabel: isUnavailable ? resolvedCloseLabel : resolvedConfirmLabel,
    currencyCode: specialService?.LoungeQuotes[0]?.currency ?? '',
    contentTitle: isUnavailable ? '' : resolvedContentTitle,
    contentDescription: isUnavailable ? '' : resolvedContentDescription,
    infoBannerText: resolvedInfoBannerText,
    passengers,
    passengerSSRDetails: mapPassengerSSRDetails(passengers, specialService, codeTypeList),
    flightTabItems,
    options,
    isUnavailable,
    unavailableTitle: isUnavailable ? resolvedIncludedTitle : undefined,
    unavailableDescription: isUnavailable ? resolvedIncludedDescription : undefined,
    unavailableVariant: isUnavailable ? 'centered' : undefined,
    unavailableIcon: isUnavailable ? (
      <IconLoungeModal size={48} aria-label="No inventory" />
    ) : undefined,
  };
};
